import React, { useState } from 'react';
import { Button, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { UserPlus, RotateCw } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { getFubErrorMessage } from './utils/errorHelpers';

interface FubTeamMembersSyncProps {
  selectedOrg?: Organization;
  isConfigured: boolean;
  hasCustomFields: boolean;
  hasStagesAllowlist?: boolean;
  syncFubTeamMembers: (orgId: string, createMissing?: boolean) => Promise<any>;
  isSyncingTeamMembers: boolean;
  onTeamMembersSync: () => void;
  isOpen?: boolean;
}

export const FubTeamMembersSync: React.FC<FubTeamMembersSyncProps> = ({
  selectedOrg,
  isConfigured,
  hasCustomFields,
  hasStagesAllowlist = false,
  syncFubTeamMembers,
  isSyncingTeamMembers,
  onTeamMembersSync,
  isOpen = false,
}) => {
  const [syncResult, setSyncResult] = useState<any>(null);
  const [hasAttemptedSync, setHasAttemptedSync] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const { showToast } = useToast();

  // Function to sync team members
  const handleSyncTeamMembers = async (createMissing: boolean = true) => {
    if (!selectedOrg?.id) return;

    try {
      setSyncError(null);
      const result = await syncFubTeamMembers(selectedOrg.id, createMissing);

      if (result?.success) {
        setSyncResult(result);
        setHasAttemptedSync(true);
        showToast({
          message: 'Team members synced successfully',
          variant: 'success',
        });

        // Notify parent component
        onTeamMembersSync();
      } else {
        const errorMsg = getFubErrorMessage(result, 'Failed to sync team members');
        setSyncError(errorMsg);
        showToast({
          message: errorMsg,
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error syncing team members:', error);
      const errorMsg = getFubErrorMessage(error, 'Failed to sync team members');
      setSyncError(errorMsg);
      showToast({
        message: errorMsg,
        variant: 'error',
      });
    }
  };

  // If API key is not configured, custom fields are not set up, or stages are not configured, don't show this component
  if (!isConfigured || !hasCustomFields || !hasStagesAllowlist) {
    return null;
  }

  return (
    <div>
      <div className="space-y-4">
        <Typography variant="p">
          Sync team members from Follow Up Boss to Speculo AI. This ensures that leads can be
          properly assigned to team members.
        </Typography>

        {syncResult ? (
          <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-md">
            <Typography variant="h3" className="text-lg font-medium mb-2">
              Sync Results
            </Typography>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Total FUB Users:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.total_fub_users || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Created:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.created || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Updated:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.updated || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Matched:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.matched || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Skipped:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.skipped || 0}
                </Typography>
              </div>
              <div>
                <Typography variant="p" className="text-sm font-medium">
                  Errors:
                </Typography>
                <Typography variant="p" className="text-sm">
                  {syncResult.summary?.errors || 0}
                </Typography>
              </div>
            </div>

            {syncResult.details?.created?.length > 0 && (
              <div className="mb-4">
                <Typography variant="p" className="text-sm font-medium mb-1">
                  Created Team Members:
                </Typography>
                <div className="max-h-40 overflow-y-auto bg-white dark:bg-white/5 p-2 rounded border dark:border-white/10 text-sm">
                  {syncResult.details.created.map((member: any, index: number) => (
                    <div key={index} className="mb-1 pb-1 border-b border-gray-100 dark:border-white/10 last:border-0">
                      {member.name} ({member.email})
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              variant="outline"
              type="button"
              onClick={() => handleSyncTeamMembers(true)}
              disabled={isSyncingTeamMembers}
              isLoading={isSyncingTeamMembers}
              className="flex items-center gap-1 h-8"
            >
              <RotateCw className="h-4 w-4" />
              Sync Again
            </Button>
          </div>
        ) : syncError ? (
          <div className="text-center py-4">
            <Typography variant="p" className="text-sm text-red-500">
              {syncError}
            </Typography>
            <Button
              variant="outline"
              type="button"
              onClick={() => handleSyncTeamMembers(true)}
              className="mt-2 h-8"
            >
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              variant="outline"
              type="button"
              onClick={() => handleSyncTeamMembers(true)}
              disabled={isSyncingTeamMembers}
              isLoading={isSyncingTeamMembers}
              className="flex items-center gap-1 h-8"
            >
              <UserPlus className="h-4 w-4" />
              Sync Team Members
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
